import { orderBy, sortBy, uniq, uniqBy } from 'lodash';
import { createDraft, finishDraft } from 'immer';
import type { ClientId } from '../ClientId';
import { generateClientId } from '../ClientId';
import type { NONETYPE } from '../../../lib/util';
import { NONE, filteredMap, sliceBy } from '../../../lib/util';
import type {
  ComputeScheduleMetricsParams,
  ScheduleOptimizationOpts,
} from '../Core';
import {
  deriveEventModifications,
  getOrderLDIndices,
  mergeEntryModifications,
} from '../Core';
import { getVesselCapacities } from '../Vessel';
import {
  computeScheduleMetrics,
  hasMetricsViolations,
} from '../scheduleMetrics';
import { vesselFullfillsAllOrderRequirements } from './getSuggestions';
import type { CalculationConfig } from '../ScheduleDataContext';
import type { Vessel } from '../Vessel';
import type { TCTimelineEvent, Events, TimeData } from '../TimeData';
import {
  getEventEndLocationId,
  getEventStartLocationId,
  isPortCallArrivalEventTimeData,
  isPortCallBerthingEventTimeData,
  isPortCallArrivalEvent,
  isCargoEventTimeData,
  isLoadEventTimeData,
  isOffHireEventTimeData,
  isSingleEventTimeData,
} from '../TimeData';
import type { ScheduleDataContext } from '../ScheduleDataContext';
import { dToMsOpt } from '../../../lib/time';
import type { TCEventTimeline } from '../Timelines';
import type { LocationPair } from '../../distances/context';
import type { ScenarioOrder } from '../../../lib/planningData';

import type { InternalUserSchedule } from '../../Scenario';
import {
  singleScenarioReducer,
  updateScenarioFromChangeMaps,
} from '../../Scenario';
import type {
  SuggestionSchedule,
  UserSchedule,
} from '@seaber/common/core/scenario';
import { applyNewCargoAllocations } from '../Core.cargo-allocation';
import { Objective } from './VoyageSuggestionParams';
import type { ScenarioImprovementsParams } from '../../ScenarioImprovementParams';
import type { ViolationIndicatorBasis } from '../../../state/planning/options';
import { mapGroupBy } from '@seaber/common/lib/collections';
import type { CargoAllocationOptions } from './Suggestions';
import { type useSpecialRequirements } from '../../hooks/useSpecialRequirements';

export type SwapInfo = {
  vesselId: number;
  previousPortId: number;
  orderIds: number[];
};

const swapInfoFromBlock = (block: MovableEventBlock): SwapInfo => ({
  vesselId: block.timeline.vessel.id,
  previousPortId: block.previousPortId,
  orderIds: block.orders.map((o) => o.id),
});

/** An event block is a continuous sequence of events that can be moved together onto another vessel or timing. */
type MovableEventBlock = {
  timeline: TCEventTimeline;
  previousPortId: number;
  startPortId: number | null;
  endPortId: number | null;
  /** Based on the first event in the block */
  avoidsChannels: boolean;
  /** Ballast distance in the current schedule */
  initialDistance: number;
  /** Best ballast distance from any origin port to voyage start */
  minimumDistance: number;
  /** May be an empty array */
  events: TimeData<TCTimelineEvent>[];
  allTimelineEvents: TimeData<TCTimelineEvent>[];
  previousEvents: TimeData<TCTimelineEvent>[];
  previousOrders: ScenarioOrder[];
  orders: ScenarioOrder[];
  maxUsedMassCapacity: number;
  maxUsedVolumeCapacity: number;
};

/** Cleans up order related data in scenario when the order is to be moved to another timeline together with port calls etc.
 * If not moved together with port calls then more cleanup needs to be done and this method is not suitable. */
const deleteAssignmentsForMove = (
  draft: InternalUserSchedule,
  orders: ScenarioOrder[],
  { deleteShipments }: { deleteShipments: boolean },
) => {
  for (const order of orders) {
    if (deleteShipments) {
      singleScenarioReducer(draft, {
        type: 'UPDATE_ORDER',
        orderId: order.id,
        patch: { shipmentId: null },
      });
    }

    singleScenarioReducer(draft, {
      type: 'SET_ORDER_VOYAGE_CHARTER',
      orderId: order.id,
      voyageCharterId: null,
    });

    singleScenarioReducer(draft, {
      type: 'DELETE_ASSIGNMENT',
      orderId: order.id,
    });
  }
};

export type ScenarioImprovement = {
  scenario: SuggestionSchedule;
  swap: [SwapInfo, SwapInfo];
  improvedDistance: number;
};

export const getScenarioSwapImprovementKey = (
  blockA: SwapInfo,
  blockB: SwapInfo,
): string => {
  const strA = `${blockA.vesselId}-${
    blockA.previousPortId
  }-${blockA.orderIds.join(',')}`;
  const strB = `${blockB.vesselId}-${
    blockB.previousPortId
  }-${blockB.orderIds.join(',')}`;
  return `${strA}/${strB}`;
};

export const getScenarioImprovements = (
  originalScenario: UserSchedule,
  originalTimelines: TCEventTimeline[],
  calculationConfig: CalculationConfig,
  scheduleContext: ScheduleDataContext,
  {
    startAt = Date.now(),
    maxResults = 1,
    ignoredSwapSuggestions = [],
    cargoAllocationOptions,
    cargoExclusions,
    violationLimitsD,
    violationBasis,
    specialRequirements,
  }: ScenarioImprovementsParams & {
    startAt?: number;
    maxResults?: number;
    ignoredSwapSuggestions?: string[];
    cargoAllocationOptions: CargoAllocationOptions;
    violationBasis: ViolationIndicatorBasis;
    specialRequirements: ReturnType<typeof useSpecialRequirements>;
  },
  _onProgress: (improvements: ScenarioImprovement[]) => Promise<void>,
): ScenarioImprovement[] => {
  const distances = scheduleContext.distances;
  const results: ScenarioImprovement[] = [];

  const allPossibleLocationPairs = getRequiredLocationPairs(
    originalTimelines,
    startAt,
  );
  const allOriginPorts = uniq(allPossibleLocationPairs.map((p) => p[0]));

  /** Returns all the ways the timeline may be split in half so the latter part could be moved to another vessel */
  const getTimelineMovableBlocks = (
    timeline: TCEventTimeline,
  ): MovableEventBlock[] => {
    const allEvents = timeline.voyageGroups[0]
      .events as TimeData<TCTimelineEvent>[];

    const timelineOrders = filteredMap(allEvents, (td) =>
      isCargoEventTimeData(td) ? td.event.order : NONE,
    );
    const timelineOrdersByIncomingVoyageCharterId = mapGroupBy(
      timelineOrders,
      (order) => order.incomingVoyageCharterRelation?.voyageCharterId ?? null,
    );
    const timelineOrdersByShipmentId = mapGroupBy(
      timelineOrders,
      (order) => order.shipmentId,
    );

    const allVoyages = getEmptyToEmptyVoyages(allEvents);

    const voyagesWithStats = allVoyages
      .map((events, i) => {
        const orders = filteredMap(events, (td) =>
          isLoadEventTimeData(td) ? td.event.order : NONE,
        );
        const maxUsedMassCapacity = Math.max(
          ...events.map((td) => td.usedMassCapacity),
        );
        const maxUsedVolumeCapacity = Math.max(
          ...events.map((td) => td.usedVolumeCapacity),
        );
        const prevEvent = allEvents.at(events[0].index - 1);
        const isMovable =
          (prevEvent ? prevEvent.end >= startAt : events[0].start >= startAt) &&
          !events.find(isOffHireEventTimeData) &&
          !events.find(isSingleEventTimeData);
        return {
          voyageIndex: i,
          isMovable,
          orders,
          maxUsedMassCapacity,
          maxUsedVolumeCapacity,
          events,
        };
      })
      .concat(
        // Add an empty voyage at the end which turns a 1-to-1 swap effectively into a move of a voyage to another timeline
        {
          voyageIndex: allVoyages.length,
          isMovable: true,
          orders: [],
          maxUsedMassCapacity: 0,
          maxUsedVolumeCapacity: 0,
          events: [],
        },
      );

    return filteredMap(
      voyagesWithStats,
      ({ voyageIndex }): MovableEventBlock | NONETYPE => {
        const blockVoyages = voyagesWithStats.slice(voyageIndex);
        if (!blockVoyages.every((v) => v.isMovable)) {
          // We move all voyages as a single unit, each of them must be movable
          return NONE;
        }
        const previousVoyages = voyagesWithStats.slice(0, voyageIndex);
        // All the events before this voyage
        const previousEvents = previousVoyages.flatMap((v) => v.events);
        // All the events starting from this voyage until the end of the timeline
        const blockEvents = blockVoyages.flatMap((v) => v.events);
        const firstEvent = blockEvents.at(0)?.event ?? null;
        const lastEvent = blockEvents.at(-1)?.event ?? null;
        const startPortId = firstEvent && getEventStartLocationId(firstEvent);
        const endPortId = lastEvent && getEventEndLocationId(lastEvent);
        const avoidsChannels =
          !!firstEvent &&
          isPortCallArrivalEvent(firstEvent) &&
          firstEvent.legAvoidsChannels;
        const previousEvent = previousEvents.at(-1) ?? null;
        if (!previousEvent) {
          // Swapping to beginning of empty timeline isn't meaningful
          return NONE;
        }
        const previousPortId = getEventEndLocationId(previousEvent.event);

        const initialDistance =
          previousPortId === null || startPortId === null
            ? 0
            : (distances.get(previousPortId, startPortId, avoidsChannels)
                ?.dist ?? 0);
        const minimumDistance =
          startPortId === null
            ? 0
            : Math.min(
                ...allOriginPorts.map((originId) => {
                  const d =
                    distances.get(originId, startPortId, avoidsChannels)
                      ?.dist ?? Infinity;
                  return d;
                }),
              );

        const previousOrders = previousVoyages.flatMap((v) => v.orders);
        const blockOrders = blockVoyages.flatMap((v) => v.orders);
        const allOrdersOnVoyageCharters = uniqBy(
          blockOrders.flatMap((order) =>
            order.incomingVoyageCharterRelation?.voyageCharterId !== undefined
              ? (timelineOrdersByIncomingVoyageCharterId.get(
                  order.incomingVoyageCharterRelation.voyageCharterId,
                ) ?? [])
              : [order],
          ),
          (order) => order.id,
        );
        if (blockOrders.length !== allOrdersOnVoyageCharters.length) {
          // Cannot move orders which would split a voyage charter onto two vessels
          return NONE;
        }
        const allOrdersOnShipments = uniqBy(
          blockOrders.flatMap((order) =>
            order.shipmentId !== null
              ? (timelineOrdersByShipmentId.get(order.shipmentId) ?? [])
              : [order],
          ),
          (order) => order.id,
        );
        if (blockOrders.length !== allOrdersOnShipments.length) {
          // Cannot move orders which would split a shipment onto two vessels
          return NONE;
        }

        const blockMaxUsedMassCapacity = Math.max(
          ...blockVoyages.map((v) => v.maxUsedMassCapacity),
        );
        const blockMaxUsedVolumeCapacity = Math.max(
          ...blockVoyages.map((v) => v.maxUsedVolumeCapacity),
        );

        return {
          timeline,
          previousPortId,
          startPortId,
          endPortId,
          avoidsChannels,
          initialDistance,
          minimumDistance,
          previousEvents,
          events: blockEvents,
          allTimelineEvents: allEvents,
          previousOrders,
          orders: blockOrders,
          maxUsedMassCapacity: blockMaxUsedMassCapacity,
          maxUsedVolumeCapacity: blockMaxUsedVolumeCapacity,
        };
      },
    );
  };

  /** Blocks of events at the end of vessel's timeline that can be moved together onto another vessel */
  const movableBlocksByTimeline = new Map(
    originalTimelines.map((timeline) => [
      timeline.id,
      getTimelineMovableBlocks(timeline),
    ]),
  );

  /** Blocks with a long ballast relative to minimum ballast come first */
  const blocksSortedByImprovementPotential = sortBy(
    [...movableBlocksByTimeline.values()].flat(),
    (block) => block.minimumDistance - block.initialDistance,
  );

  /** Makes sure that cargo fits and that vessel meets all orders requirements */
  const isBlockPossibleOnVessel = (
    block: MovableEventBlock,
    vessel: Vessel,
  ) => {
    const vesselCapacities = getVesselCapacities(vessel);
    return (
      block.maxUsedMassCapacity <= vesselCapacities.massCapacity &&
      block.maxUsedVolumeCapacity <= vesselCapacities.volumeCapacity &&
      block.orders.every((order) =>
        vesselFullfillsAllOrderRequirements(vessel, order, specialRequirements),
      )
    );
  };

  const validSwapPairs: [
    MovableEventBlock,
    MovableEventBlock,
    improvement: number,
  ][] = [];
  for (let i = 0; i < blocksSortedByImprovementPotential.length; i++) {
    const blockA = blocksSortedByImprovementPotential[i];
    for (let j = i + 1; j < blocksSortedByImprovementPotential.length; j++) {
      const blockB = blocksSortedByImprovementPotential[j];
      if (
        // Cannot swap blocks on the same timeline
        blockA.timeline === blockB.timeline ||
        // Swapping to beginning of empty timeline isn't meaningful (we don't know where the vessel had been originally)
        blockA.previousPortId === null ||
        blockB.previousPortId === null ||
        // Swapping two empty blocks does nothing
        (blockA.startPortId === null && blockB.startPortId === null) ||
        // Make sure that allocated cargo fits the vessel and the vessel is compatible with all parcels
        !isBlockPossibleOnVessel(blockA, blockB.timeline.vessel) ||
        !isBlockPossibleOnVessel(blockB, blockA.timeline.vessel)
      ) {
        continue;
      }

      const swapSuggestionKey = getScenarioSwapImprovementKey(
        swapInfoFromBlock(blockA),
        swapInfoFromBlock(blockB),
      );
      if (ignoredSwapSuggestions.includes(swapSuggestionKey)) {
        continue;
      }

      const newDistanceA =
        blockA.startPortId === null
          ? 0
          : (distances.get(
              blockB.previousPortId,
              blockA.startPortId,
              blockA.avoidsChannels,
            )?.dist ?? Infinity);
      const newDistanceB =
        blockB.startPortId === null
          ? 0
          : (distances.get(
              blockA.previousPortId,
              blockB.startPortId,
              blockB.avoidsChannels,
            )?.dist ?? Infinity);
      const totalInitialDistance =
        blockA.initialDistance + blockB.initialDistance;
      const totalNewDistance = newDistanceA + newDistanceB;
      const improvement = totalInitialDistance - totalNewDistance;

      validSwapPairs.push([blockA, blockB, improvement]);
    }
  }

  const swapPairsSortedByPotential: [
    MovableEventBlock,
    MovableEventBlock,
    improvement: number,
  ][] = orderBy(
    validSwapPairs,
    ([_blockA, _blockB, improvement]) => improvement,
    ['desc'],
  );

  /** Returns events to move, but ensures that no shipment gets split onto two different vessels. Also clears voyagecharter and assigns new vessel. */
  const prepareEventsForMoveToTimeline = (
    block: MovableEventBlock,
    targetTimelineBlock: MovableEventBlock,
  ): TCTimelineEvent[] => {
    const previousShipmentIds = new Set(
      block.previousOrders.map((o) => o.shipmentId),
    );
    const shipmentIdChangeMap = new Map<number | ClientId, ClientId>();
    const updatedOrderMap = new Map<number, ScenarioOrder>();
    return block.events.map((td, i): TCTimelineEvent => {
      const event: TCTimelineEvent = {
        ...td.event,
        vesselId: targetTimelineBlock.timeline.vessel.id,
        vessel: targetTimelineBlock.timeline.vessel,
        seaDurationOverride: i === 0 ? null : td.event.seaDurationOverride,
        // Clear all but ETB startOverrides. We keep ETB because it's often set to wait for laycan start.
        startOverride: isPortCallBerthingEventTimeData(td)
          ? td.event.startOverride
          : null,
        delayUntil: i === 0 ? startAt : undefined,
      };
      if ('order' in event) {
        const { order } = event;
        let updatedOrder = updatedOrderMap.get(order.id);
        if (!updatedOrder) {
          updatedOrder = {
            ...order,
            outgoingVoyageCharterRelation: null,
          };

          if (
            order.shipmentId !== null &&
            previousShipmentIds.has(order.shipmentId)
          ) {
            // This block never triggers if the block moving logic doesn't break shipments (which is the case at the time of writing)
            const newShipmentId =
              shipmentIdChangeMap.get(order.shipmentId) ?? generateClientId();
            shipmentIdChangeMap.set(order.shipmentId, newShipmentId);
            updatedOrder = {
              ...updatedOrder,
              shipmentId: newShipmentId,
            };
          }
        }

        return {
          ...event,
          order: updatedOrder,
        };
      }
      return event;
    });
  };

  const violationLimitsMs = {
    load: {
      late: dToMsOpt(violationLimitsD.load.late) ?? null,
      early: dToMsOpt(violationLimitsD.load.early) ?? null,
    },
    discharge: {
      late: dToMsOpt(violationLimitsD.discharge.late) ?? null,
      early: dToMsOpt(violationLimitsD.discharge.early) ?? null,
    },
  };

  for (const [blockA, blockB, improvedDistance] of swapPairsSortedByPotential) {
    if (results.length >= maxResults) {
      break;
    }

    let newEventsA = [
      ...blockA.previousEvents.map((td) => td.event),
      ...prepareEventsForMoveToTimeline(blockB, blockA),
    ];
    let newEventsB = [
      ...blockB.previousEvents.map((td) => td.event),
      ...prepareEventsForMoveToTimeline(blockA, blockB),
    ];

    if (cargoAllocationOptions.allocateCargo) {
      newEventsA = applyNewCargoAllocations(
        newEventsA,
        blockB.orders.map((o) => o.id),
        cargoAllocationOptions,
      );
      newEventsB = applyNewCargoAllocations(
        newEventsB,
        blockA.orders.map((o) => o.id),
        cargoAllocationOptions,
      );
    }

    const getSolutionMetrics = (
      newEvents: TCTimelineEvent[],
      targetBlock: MovableEventBlock,
      sourceBlock: MovableEventBlock,
    ) => {
      const deriveParams: ComputeScheduleMetricsParams = {
        vessel: targetBlock.timeline.vessel,
        context: scheduleContext,
        config: calculationConfig,
        now: startAt,
      };

      const optimizationOpts: ScheduleOptimizationOpts = {
        objective: Objective.ShortestBallast,
        countIdleFromIndex: targetBlock.previousEvents.length,
        // todoish: could use a separate param for this
        maximumDelayDeltaMs: Math.max(
          violationLimitsMs.load.late ?? 0,
          violationLimitsMs.discharge.late ?? 0,
        ),
        violationLimitsMs,
        maxBallastDistanceNm: Infinity,
        respectCargoExclusions: cargoExclusions === 'exclude',
      };

      const orders = sourceBlock.orders;
      const allAvailableOrderIds = new Set(orders.map((o) => o.id));
      return computeScheduleMetrics(
        {
          schedule: newEvents,
          assignments: getOrderLDIndices(newEvents, orders),
        },
        null,
        deriveParams,
        optimizationOpts,
        allAvailableOrderIds,
        violationBasis,
      );
    };

    // We could use these and do some comparison, but not used right now
    // const originalMetricsA = getSolutionMetrics(blockA.allTimelineEvents.map((td) => td.event)), blockA, blockA);
    // const originalMetricsB = getSolutionMetrics(blockB.allTimelineEvents.map((td) => td.event)), blockB, blockB);

    // Evaluate the solution metrics for both blocks. Currenly only used to verify that we don't have high violations.
    const metricsA = getSolutionMetrics(newEventsA, blockA, blockB);
    const metricsB = getSolutionMetrics(newEventsB, blockB, blockA);
    if (hasMetricsViolations(metricsA) || hasMetricsViolations(metricsB)) {
      continue;
    }

    const eventModificationsA = deriveEventModifications(
      newEventsA,
      [...blockA.previousEvents, ...blockA.events].map((td) => td.event),
      blockA.timeline.vessel,
      scheduleContext,
      calculationConfig,
      new Map(),
      { firstChangedIndex: blockA.previousEvents.length },
    );
    const eventModificationsB = deriveEventModifications(
      newEventsB,
      [...blockB.previousEvents, ...blockB.events].map((td) => td.event),
      blockB.timeline.vessel,
      scheduleContext,
      calculationConfig,
      new Map(),
      { firstChangedIndex: blockB.previousEvents.length },
    );
    const newScenarioDraft = createDraft(originalScenario);

    Object.assign(newScenarioDraft, {
      id: null,
      isReadOnly: false,
    });

    deleteAssignmentsForMove(newScenarioDraft, blockA.orders, {
      deleteShipments: false,
    });
    deleteAssignmentsForMove(newScenarioDraft, blockB.orders, {
      deleteShipments: false,
    });

    const modifications = mergeEntryModifications(
      eventModificationsA,
      eventModificationsB,
    );
    updateScenarioFromChangeMaps(newScenarioDraft, modifications);

    /** TODO: what was not handled here (but would be ideal if it were):
     * - updating berth durations and port costs (we reuse old ones, but new vessel could require different values)
     */

    const finalized: SuggestionSchedule = {
      ...finishDraft(newScenarioDraft),
      clientId: generateClientId(),
      baseScenarioId: originalScenario.clientId,
      name: `${originalScenario.name}-${results.length + 1}`,
      shared: false,
      type: 'suggestion',
    };
    results.push({
      scenario: finalized,
      swap: [swapInfoFromBlock(blockA), swapInfoFromBlock(blockB)],
      improvedDistance,
    });
  }

  return results;
};

const getEmptyToEmptyVoyages = <T extends Events>(
  tds: TimeData<T>[],
  allowSplitOnAvoidedChannels = false,
): TimeData<T>[][] => {
  return sliceBy(tds, (e, i) => {
    const prev = tds.at(i - 1);
    return (
      !prev ||
      (!e.laden &&
        getEventEndLocationId(prev.event) !==
          getEventStartLocationId(e.event) &&
        (allowSplitOnAvoidedChannels ||
          // By default no split when original leg avoids channels because it's hard to know if leg from other origin should also do so
          !(isPortCallArrivalEventTimeData(e) && e.event.legAvoidsChannels)))
    );
  });
};

/** Returns all location pairs which may become legs after swapping voyages between vessels */
export const getRequiredLocationPairs = (
  timelines: TCEventTimeline[],
  /** Only account for voyages which start after this date */
  startAt = Date.now(),
): LocationPair[] => {
  return uniqBy(
    timelines.flatMap((timeline) => {
      const allVoyages = getEmptyToEmptyVoyages(
        timeline.voyageGroups[0].events,
      );
      const allEvents = timeline.voyageGroups[0].events;
      const voyageStarts = uniq(
        filteredMap(allVoyages, (events) => {
          const firstEvent = events[0];
          const prevEvent = allEvents.at(firstEvent.index - 1);
          // Ballast to voyage must start in the future to be eligible for moving
          return (prevEvent ? prevEvent.end : firstEvent.start) >= startAt
            ? firstEvent
            : NONE;
        }),
      );
      /** Where a voyage ends, it's an origin for the next one */
      const voyageOrigins = uniq(
        filteredMap(allVoyages, (events) => {
          const lastEvent = events[events.length - 1];
          return lastEvent.end >= startAt ? lastEvent : NONE;
        }),
      );

      return voyageOrigins.flatMap((origin) =>
        voyageStarts.map(
          (start): LocationPair =>
            [
              getEventEndLocationId(origin.event),
              getEventStartLocationId(start.event),
              isPortCallArrivalEvent(start.event) &&
                start.event.legAvoidsChannels,
              null,
              'null', // TODO: should we consider noGoZones for scenario improvements?
            ] as LocationPair,
        ),
      );
    }),
    (locationPair: LocationPair) => locationPair.join('-'),
  );
};
